import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import { useDisableNumberInputScroll } from '../../../app/useDisableNumberInputScroll';
import { Input, NumericTextBox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";

export const AdministratorVenueEventShorting = (props) => {
    //Use the custom hook to disable scrolling in number inputs
    useDisableNumberInputScroll();

    const dispatch = useDispatch();

    const baseDefaultQuantity = 4;

    const quantities = [
        { name: "Quantity", value: 0 },
        { name: "1", value: 1 },
        { name: "2", value: 2 },
        { name: "3", value: 3 },
        { name: "4", value: 4 },
        { name: "5", value: 5 },
        { name: "6", value: 6 },
        { name: "7", value: 7 },
        { name: "8", value: 8 },
        { name: "9", value: 9 },
        { name: "10", value: 10 },
        { name: "11", value: 11 },
        { name: "12", value: 12 },
    ];

    const [defaultQuantity, setDefaultQuantity] = useState(baseDefaultQuantity);
    const [selectedQuantity, setSelectedQuantity] = useState(quantities[baseDefaultQuantity]);
    const [enteredSectionName, setEnteredSectionName] = useState("GA");
    const [enteredLowSeatNumber, setEnteredLowSeatNumber] = useState(undefined);
    const [enteredCost, setEnteredCost] = useState(undefined);
    const [enteredPrice, setEnteredPrice] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);

    const quantityChanged = (event) => {
        setSelectedQuantity(event.target.value);
    };

    const sectionNameChanged = (event) => {
        setEnteredSectionName(event.value);
    };

    const lowSeatNumberChanged = (event) => {
        setEnteredLowSeatNumber(event.value);
    };

    const costChanged = (event) => {
        setEnteredCost(event.value);
    };

    const priceChanged = (event) => {
        setEnteredPrice(event.value);
    };

    const createInventory = () => {
        if (!enteredSectionName) {
            addNotificationWithTimeout(dispatch, "error", "You must enter a section name.");   
            return;
        }

        if (selectedQuantity.value === 0) {
            addNotificationWithTimeout(dispatch, "error", "You must select a quantity.");   
            return;
        }

        if (window.confirm("Are you sure you want to short this event?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            venueEvents: [{
                venueEventKey: props.venueEventKey,
            }],
            sectionName: enteredSectionName,
            quantity: selectedQuantity.value,
            lowSeatNumber: enteredLowSeatNumber,
            cost: enteredCost,
            price: enteredPrice,
        };
    
        fetch(`/api/administrator/pos/shortevents`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Shorted event successfully");     
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);  
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const getDefaultShortingQuantity = () => {
        setIsLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
        };

        fetch(`/api/administrator/venueeventdefaultshortingquantity`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                if (result.defaultShortingQuantity) {
                    setDefaultQuantity(result.defaultShortingQuantity);
                    setSelectedQuantity(quantities[result.defaultShortingQuantity]);
                    getShortingSectionName(result.defaultShortingQuantity);
                }
                else {
                    getShortingSectionName();
                }
            })
            .catch(error => {
                getShortingSectionName();
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const getShortingSectionName = (defaultShortingQuantity) => {
        setIsLoading(true);

        const dataItem = {
            venueEventKey: props.venueEventKey,
        };
    
        fetch(`/api/administrator/venueeventshortingsectionname`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                getEventShortingDetails(result.sectionName, defaultShortingQuantity);
                setEnteredSectionName(result.sectionName);                
            })
            .catch(error => {
                getEventShortingDetails(undefined, defaultShortingQuantity);
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    };

    const getEventShortingDetails = (section, defaultShortingQuantity) => {
        if (!section) {
            section = enteredSectionName;
        }
        if (!defaultShortingQuantity) {
            defaultShortingQuantity = defaultQuantity;
        }

        const dataItem = {
            venueEventKey: props.venueEventKey,
            sectionName: section,
        };

        fetch(`/api/administrator/pos/shortedevent`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                if (result.lowSeatNumber) {
                    setEnteredLowSeatNumber(result.lowSeatNumber);
                }

                if (result.cost) {
                    setEnteredCost(result.cost);
                }

                if (result.price) {
                    setEnteredPrice(result.price);
                }

                if (result.quantity
                    && result.quantity > 0
                    && result.quantity <= defaultShortingQuantity) {
                    const shortingQuantity = defaultShortingQuantity - result.quantity;
                    setSelectedQuantity(quantities[shortingQuantity]);
                }
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (props.venueEventKey) {
            getDefaultShortingQuantity();
        }
    }, []);

    return (
        <>
            {isLoading && <LoadingPanel /> }

            <div className="mt-3 d-flex flex-row">   
                <div className="me-2">
                    <Input 
                        id={"sectionName"} 
                        placeholder="Section name"
                        onChange={sectionNameChanged}
                        value={enteredSectionName} />
                </div>
                <div className="me-2">
                    <NumericTextBox 
                        id={"lowSeatNumber"} 
                        placeholder="Low seat"
                        onChange={lowSeatNumberChanged}
                        value={enteredLowSeatNumber}
                        spinners={false}
                        format="n0"
                        width={90}
                        min={1}
                        max={2000000000} />
                </div>
                <div className="me-2">
                    <NumericTextBox
                        id={"cost"}
                        placeholder="Cost"
                        onChange={costChanged}
                        value={enteredCost}
                        spinners={false}
                        format="n2"
                        width={90}
                        min={1}
                        max={10000} />
                </div>
                <div className="me-2">
                    <NumericTextBox
                        id={"price"}
                        placeholder="Price"
                        onChange={priceChanged}
                        value={enteredPrice}
                        spinners={false}
                        format="n2"
                        width={90}
                        min={1}
                        max={10000} />
                </div>
                <div className="me-2">
                    <DropDownList
                        id="quantity"
                        textField="name"
                        dataItemKey="value"
                        data={quantities}
                        value={selectedQuantity}
                        onChange={quantityChanged}
                    />
                </div>
                <div>
                    <Button 
                        themeColor="primary" 
                        type={"button"} 
                        icon="plus"
                        title="Short Event"
                        onClick={createInventory}>
                        Short Event
                    </Button>
                </div>
            </div>
        </>
    );
}